import { useTheme as useHelixTheme } from '@talos/helix';
import type { ThemeTypes } from '@talos/kyoko';
import {
  abbreviateId,
  ACTION,
  Box,
  ENV_COLORS,
  FormControlSizes,
  Icon,
  IconName,
  IndicatorBadge,
  MixpanelEvent,
  MixpanelEventProperty,
  Text,
  ThemeToggle,
  useAuthContext,
  useMixpanel,
} from '@talos/kyoko';
import { startTransition, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useBackendVersion, useUser } from '../../../hooks';
import { useDisableMobileLayoutForSession } from '../../../hooks/useMobileLayoutState';
import { useRoleAuth } from '../../../hooks/useRoleAuth';
import { useThemeTypePreference } from '../../../hooks/useThemeTypePreference';
import { envConfig } from '../../../utils/envConfig';
import { appVersion, isNonReleaseVersion } from '../../../utils/version';
import { MobileNavOrganizationSelect } from './MobileNavOrganizationSelect';
import {
  MobileFooterButton,
  MobileFooterTop,
  MobileThemeRow,
  MobileUserFooter,
  MobileUserNameRow,
  MobileUserSummary,
  MobileVersionText,
} from './styles';

export function MobileNavMenuFooter() {
  const user = useUser();
  const auth = useAuthContext();
  const mixpanel = useMixpanel();
  const { isAuthorized } = useRoleAuth();
  const { themeType, setThemeType } = useThemeTypePreference();
  const { setType: setHelixThemeType } = useHelixTheme();
  const { backendVersion, versionRef } = useBackendVersion();
  const location = useLocation();
  const [, setIsMobileLayoutDisabledForSession] = useDisableMobileLayoutForSession();
  const env = envConfig.VITE_AVA_ENV;
  const uiVersion = import.meta.env.VITE_NPM_PACKAGE_VERSION;
  const isProdEnv = env === 'prod';
  const displayVersion = appVersion(uiVersion, isProdEnv);
  const gitHash = import.meta.env.VITE_GIT_HASH;
  const uiVersionLabel =
    isNonReleaseVersion(uiVersion) && gitHash ? `UI #${abbreviateId(gitHash)}` : `UI v${displayVersion}`;
  const versionParts = [backendVersion, uiVersionLabel].filter(Boolean);
  const envPrefix = isProdEnv ? '' : `${env.toUpperCase()} `;
  const versionText = `${envPrefix}${versionParts.join(' / ')}`;

  const handleThemeChange = useCallback(
    (nextThemeType: ThemeTypes) => {
      setThemeType(nextThemeType);
      setHelixThemeType(nextThemeType);
      mixpanel.track(MixpanelEvent.ChangeThemeType, { [MixpanelEventProperty.Type]: nextThemeType });
    },
    [mixpanel, setHelixThemeType, setThemeType]
  );

  const handleLogout = useCallback(() => {
    mixpanel.track(MixpanelEvent.LogOut);
    auth.logout();
  }, [auth, mixpanel]);

  const handleUseDesktopVersion = useCallback(() => {
    mixpanel.track(MixpanelEvent.ContinueToDesktopVersion, {
      [MixpanelEventProperty.Path]: location.pathname,
    });
    startTransition(() => setIsMobileLayoutDisabledForSession(true));
  }, [location.pathname, mixpanel, setIsMobileLayoutDisabledForSession]);

  return (
    <MobileUserFooter data-testid="mobile-nav-menu-user-footer">
      {isAuthorized(ACTION.USE_ORGANIZATION) ? <MobileNavOrganizationSelect /> : null}
      <MobileFooterTop>
        <MobileUserSummary ref={versionRef}>
          <MobileUserNameRow>
            <Box color="colorTextImportant" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {user?.Name}
            </Box>
            <IndicatorBadge>{user?.Organization}</IndicatorBadge>
          </MobileUserNameRow>
          <Box color="colorTextDefault" fontSize="fontSizeXs">
            {user?.Email}
          </Box>
          <MobileVersionText color={ENV_COLORS[env] ?? 'gray.090'} data-testid="mobile-nav-menu-version-text">
            {versionText}
          </MobileVersionText>
        </MobileUserSummary>
        <MobileThemeRow>
          <Icon icon={IconName.ColorSwatch} />
          <Text color="colorTextDefault">Theme</Text>
          <ThemeToggle
            ml="auto"
            themeType={themeType}
            onChangeThemeType={handleThemeChange}
            size={FormControlSizes.Default}
          />
        </MobileThemeRow>
      </MobileFooterTop>
      <MobileFooterButton
        startIcon={IconName.DeviceDesktop}
        onClick={handleUseDesktopVersion}
        data-testid="mobile-nav-menu-use-desktop-version-button"
      >
        Use desktop version
      </MobileFooterButton>
      <MobileFooterButton
        startIcon={IconName.Logout}
        onClick={handleLogout}
        data-testid="mobile-nav-menu-logout-button"
      >
        Logout
      </MobileFooterButton>
    </MobileUserFooter>
  );
}
